import { IS_VERCEL_PROD } from '@monoimin/constants';

import { DebugInfo } from '../analytics/DebugInfo';
import newRelicLog from '../api/newRelicLog';

import { isProductionEnv } from '../envUtils';

import { logUtils } from './logUtils';
import { EnhancedLogEntry, LogEntry, LogSection } from './logger.types';
import { LogFormatter } from './LogFormatter';
import { CacheDetector } from './cacheDetector';

const { printMs } = logUtils;

const SHOW_API_LOGS = process.env.SHOW_API_LOGS === 'true';

const LOG_LEVEL = {
  ERROR: 'Error',
  WARN: 'Warn',
  INFO: 'Info',
  DEBUG: 'Debug',
} as const;

type LogLevel = (typeof LOG_LEVEL)[keyof typeof LOG_LEVEL];

export class Logger {
  name: string;
  startTime: number;
  type: DebugInfo['type'];
  private disabled = true; // Inverted for easier usage
  private isoStartTime: Date;
  private lastLap: number | null = null;
  private level: LogLevel;

  constructor(name: string, type: DebugInfo['type'], enabled: boolean) {
    this.disabled = !enabled;
    this.name = name;
    this.type = type;
    this.startTime = performance.now();
    const now = new Date();
    this.isoStartTime = now;
    this.level = this.isEnabled() ? LOG_LEVEL.WARN : LOG_LEVEL.DEBUG;
  }

  private cacheDetector = new CacheDetector();

  private isEnabled(): boolean {
    return isProductionEnv();
  }

  private detectCacheSource(
    headers?: Record<string, string>
  ): EnhancedLogEntry['cacheSource'] {
    const result = this.cacheDetector.detect(headers, this.isEnabled());
    return result.source;
  }

  private getCacheSourceColor(source: EnhancedLogEntry['cacheSource']): string {
    switch (source) {
      case 'VERCEL_CACHE':
      case 'CLOUDFRONT':
        return LogFormatter.colors.info;
      case 'STORYBLOK_CDN':
        return LogFormatter.colors.warn;
      case 'STORYBLOK_ORIGIN':
        return LogFormatter.colors.error;
      default:
        return LogFormatter.colors.reset;
    }
  }

  private formatApiLogMessage({
    type,
    endpoint,
    cacheStatus,
    time,
    headers,
    cfId,
    error,
  }: LogEntry): string {
    const timeColor = LogFormatter.getTimeColor(time);
    const source = headers ? this.detectCacheSource(headers) : 'UNKNOWN';
    const url = new URL(endpoint);

    const queryParams = new URLSearchParams(url.search);
    const relevantParams = [
      'starts_with',
      'uuid',
      'by_uuids',
      'resolve_relations',
      'content_type',
      'per_page',
      'page',
    ]
      .filter((param) => queryParams.has(param))
      .map((param) => `${param}=${queryParams.get(param)}`)
      .join('&');

    const sections: LogSection[] = [
      {
        title: 'Request',
        color:
          LogFormatter.colors[
            type.toLowerCase() as keyof typeof LogFormatter.colors
          ],
        fields: {
          Type: `API ${type}`,
          Endpoint: url.pathname,
          Query: relevantParams || url.search.slice(1) || 'none',
          Duration: `${timeColor}${time}ms${LogFormatter.colors.reset}`,
          Timestamp: new Date().toISOString(),
        },
      },
      {
        title: 'Cache',
        color: this.getCacheSourceColor(source),
        fields: {
          Status: cacheStatus,
          Source: source,
          'CloudFront ID': cfId ?? undefined,
        },
      },
    ];

    if (headers) {
      sections.push({
        title: 'Headers',
        fields: {
          'x-cache': headers['x-cache'],
          'x-runtime': headers['x-runtime'],
          age: headers['age'],
          'x-request-id': headers['x-request-id'],
          'sb-be-version': headers['sb-be-version'],
        },
      });
    }

    if (error) {
      sections.push({
        title: 'Error',
        color: LogFormatter.colors.error,
        fields: {
          Message: error.message || String(error),
          Status: String(error.status || 'unknown'),
          Stack: error.stack,
        },
      });
    }

    return sections
      .map((section) => LogFormatter.formatSection(section))
      .join('\n\n');
  }

  private log(
    level: LogLevel,
    message: string,
    metadata: Record<string, any> = {}
  ) {
    // NewRelic logging in production
    if (this.isEnabled()) {
      newRelicLog({
        message,
        attributes: {
          severity: level === LOG_LEVEL.ERROR ? 'error' : 'info',
          logger_name: this.name,
          logger_type: this.type,
          ...metadata,
        },
      });
    }

    // Skip console logging in production unless it's an error or
    // INFO level with SHOW_API_LOGS enabled
    if (
      this.isEnabled() &&
      !(
        level === LOG_LEVEL.ERROR ||
        (level === LOG_LEVEL.INFO && SHOW_API_LOGS)
      )
    ) {
      return;
    }

    // Console logging
    const logFn =
      level === LOG_LEVEL.ERROR
        ? console.error
        : level === LOG_LEVEL.WARN
        ? console.warn
        : console.log;

    if (this.isEnabled()) {
      const essentialInfo = {
        endpoint: metadata?.request?.endpoint,
        status: metadata?.response?.status,
        duration: metadata?.request?.duration,
        ...(metadata?.error && {
          error: metadata.error.message,
          errorStatus: metadata.error.status,
        }),
      };

      if (Object.keys(essentialInfo).length > 0) {
        logFn(message, essentialInfo);
      } else {
        logFn(message);
      }
    } else {
      // In development, log everything
      logFn(message, metadata);
    }
  }

  error(message: string, metadata?: Record<string, any>) {
    this.log(LOG_LEVEL.ERROR, message, metadata);
  }

  warn(message: string, metadata?: Record<string, any>) {
    this.log(LOG_LEVEL.WARN, message, metadata);
  }

  info(message: string, metadata?: Record<string, any>) {
    this.log(LOG_LEVEL.INFO, message, metadata);
  }

  debug(message?: string): DebugInfo | Record<string, never> {
    if (this.disabled) {
      return {};
    }
    return {
      name: this.name,
      message,
      duration: performance.now() - this.startTime,
      start: this.isoStartTime.toISOString(),
      type: this.type,
    };
  }

  getDebugMetadata(message?: string): DebugInfo | Record<string, never> {
    if (this.disabled) {
      return {};
    }
    const props = this.debug(message);
    const entries = Object.entries(props).map(([name, value]) => [
      `x-debug-logger-${name}`,
      value,
    ]);
    return Object.fromEntries(entries);
  }

  lap(message?: string): void {
    if (this.disabled) {
      return;
    }

    // Skip non-error logs in production
    if (this.isEnabled() && !message?.includes('ERROR')) {
      return;
    }

    const lap = performance.now();
    const diff = lap - this.startTime;
    const sinceStart = printMs(diff);

    if (this.lastLap === null) {
      console.warn(`${sinceStart} | ${message}`);
    } else {
      const sinceLastLap = printMs(lap - this.lastLap);
      console.log(`${sinceLastLap} | ${message} | Total: ${sinceStart}`);
    }
    this.lastLap = lap;
  }

  logApiRequest(entry: LogEntry): void {
    if (this.disabled) {
      return;
    }

    const duration = entry.time ?? Date.now() - this.startTime;
    const message = this.formatApiLogMessage(entry);

    const source = entry.headers
      ? this.detectCacheSource(entry.headers)
      : 'UNKNOWN';

    // Prepare metrics for NewRelic
    if (entry.headers) {
      const metadata = {
        // Base request info
        url: entry.endpoint,
        vercel_url: process.env['NEXT_PUBLIC_VERCEL_URL'] || '',
        is_production: IS_VERCEL_PROD,

        // Request specific info
        request: {
          endpoint: new URL(entry.endpoint).pathname,
        },

        // Response info
        response: {
          status: entry.error?.status || 200,
        },

        // Cache details
        cache: {
          source,
          x_cache: entry.headers?.['x-cache'],
          age: entry.headers?.['age'],
          sb_be_version: entry.headers?.['sb-be-version'],
          cf_ray: entry.headers?.['cf-ray'],
          response_time: `${duration}ms`,
        },

        // Additional context
        environment: this.isEnabled() ? 'production' : 'development',
        timestamp: new Date().toISOString(),
        ...(entry.error && {
          error: {
            status: entry.error.status,
            message: entry.error.message,
            stringified: entry.error.toString(),
          },
        }),
      };

      if (entry.error) {
        this.error('Storyblok Error', metadata);
      }
    }

    if (entry.type === 'ERROR' || SHOW_API_LOGS) {
      this.lap(message);
    }
  }
}

export default Logger;
